// Request/response schemas for the product API (Plan 4b Tasks 2–4): Consulta (free, raw), Processo, Hoje, Prazos.
// Response schemas are plain zod objects: the serializer drops any key not declared here, so the free layer cannot
// leak synthesis (obligations, prazos, triage, translations) even by accident (review focus).
import { z } from 'zod';
import { Id, IsoDate } from './accounts.ts';
import { ProcessSeiSection } from './sei.ts';
import { CarResultView, ParcelFindingView, RestrictionFindingView } from './spatial.ts';

/** A8: what the "nada exige ação hoje" rest state depends on. `fresh` = every source is healthy. */
export const SourceFreshness = z.object({
  source: z.string(),
  step: z.string(),
  label: z.string(),
  healthy: z.boolean(),
  degradedToday: z.boolean(),
  lastOkDate: IsoDate.nullable(),
  /** When that last good run finished (ISO timestamp, UTC). */
  lastOkAt: z.string().nullable(),
  daysBehind: z.number().int().nullable(),
});
export type SourceFreshness = z.infer<typeof SourceFreshness>;
export const Freshness = z.object({ fresh: z.boolean(), asOf: IsoDate, sources: z.array(SourceFreshness) });
export type Freshness = z.infer<typeof Freshness>;

// --- Consulta: the free, raw layer ----------------------------------------------------------------------------------

/** An event as ANM published it: its own type code and name, the observation and the DOU extract. Nothing derived. */
export const RawEvent = z.object({
  occurredOn: IsoDate,
  code: z.number().int().nullable(),
  description: z.string().nullable(),
  observation: z.string().nullable(),
  publication: z.string().nullable(),
  /** Where the event was seen (scm_bulk = Cadastro Mineiro, inlabs / dou_monthly = DOU, sigmine_ult = SIGMINE). */
  sources: z.array(z.string()),
});
/** The withheld recent window (spec §4.3 item 1): always present, whether or not anything happened in it. */
export const WithheldWindow = z.object({ from: IsoDate, to: IsoDate, days: z.number().int() });

export const Party = z.object({ name: z.string().nullable(), document: z.string().nullable(), relation: z.string().nullable() });
export const RawTitle = z.object({
  number: z.string().nullable(),
  type: z.string().nullable(),
  status: z.string().nullable(),
  publishedOn: IsoDate.nullable(),
  expiresOn: IsoDate.nullable(),
});

export const ProcessSummary = z.object({
  id: Id,
  canonicalRef: z.string().nullable(),
  nup: z.string().nullable(),
  phase: z.string().nullable(),
  uf: z.string().nullable(),
  areaHa: z.number().nullable(),
  active: z.boolean().nullable(),
  protocolAt: z.string().nullable(),
  titular: Party.nullable(),
});

/** The SIGMINE polygon for the process map (public data), simplified for display; lon/lat, 6 decimals. */
export const ProcessGeometry = z.object({
  type: z.literal('MultiPolygon'),
  coordinates: z.array(z.array(z.array(z.tuple([z.number(), z.number()])))),
});
/** [west, south, east, north] of the full polygon. */
export const Bbox = z.tuple([z.number(), z.number(), z.number(), z.number()]);

/** The Cadastro Mineiro's record of a process beyond the summary (public data, as published) — the free layer has it
 *  too; the paid Dados ANM tab adds the Protocolo Digital filings (ProcessAnmData). */
export const AnmRecord = z.object({
  requestType: z.string().nullable(),
  /** The superintendência (unidade administrativa regional). */
  regionalUnit: z.string().nullable(),
  protocolAt: z.string().nullable(),
  priorityAt: z.string().nullable(),
  substances: z.array(z.object({ name: z.string(), use: z.string().nullable(), startOn: IsoDate.nullable(), endOn: IsoDate.nullable(), endReason: z.string().nullable() })),
  municipalities: z.array(z.object({ name: z.string(), uf: z.string().nullable() })),
  /** Every person ANM relates to the process (titular, arrendatário, RT…), current first, then history. */
  people: z.array(z.object({ name: z.string().nullable(), document: z.string().nullable(), relation: z.string().nullable(), startOn: IsoDate.nullable(), endOn: IsoDate.nullable() })),
  /** Process-to-process links (cessão, arrendamento, grupamento mineiro…); `otherProcessId` when we hold it. */
  associations: z.array(z.object({ type: z.string().nullable(), otherRef: z.string(), otherProcessId: Id.nullable(), startOn: IsoDate.nullable(), endOn: IsoDate.nullable(), note: z.string().nullable() })),
  /** Condição de propriedade do solo, as ANM records it. */
  soil: z.array(z.object({ condition: z.string().nullable(), note: z.string().nullable() })),
});
export type AnmRecord = z.infer<typeof AnmRecord>;

export const RawTimeline = z.object({
  process: ProcessSummary,
  geometry: ProcessGeometry.nullable(),
  bbox: Bbox.nullable(),
  titles: z.array(RawTitle),
  parties: z.array(Party),
  events: z.array(RawEvent),
  withheld: WithheldWindow,
  /** The Cadastro Mineiro record (Dados ANM, without the Protocolo Digital filings). */
  anm: AnmRecord,
});

export const ConsultaQuery = z.object({ q: z.string().trim().min(2).max(120) });
export const ConsultaCandidate = z.object({
  personId: Id,
  name: z.string().nullable(),
  document: z.string().nullable(),
  processCount: z.number().int(),
});
export const ConsultaResponse = z.object({
  query: z.string(),
  kind: z.enum(['processo', 'documento', 'nome', 'nenhum']),
  candidates: z.array(ConsultaCandidate),
  processes: z.array(ProcessSummary),
  /** Present when the query resolved to exactly one process. */
  timeline: RawTimeline.nullable(),
});
export const ConsultaPersonParams = z.object({ personId: Id });
export const ConsultaProcessParams = z.object({ processId: Id });
export const ConsultaProcessResponse = RawTimeline;

// --- Paid layer --------------------------------------------------------------------------------------------------------

export const Translation = z.object({
  what: z.string(),
  means: z.string(),
  ifIgnored: z.string().nullable(),
  legalBasis: z.string().nullable(),
  reviewState: z.enum(['rascunho', 'revisado', 'validado_juridicamente', 'automatico']),
});
export const TimelineEvent = RawEvent.extend({
  id: Id,
  kind: z.enum(['PUBLISHED_ACT', 'FILING', 'INTERNAL']),
  status: z.enum(['provisional', 'confirmed', 'superseded']),
  translation: Translation,
  /** The SEI documents linked to this event (ofício number + year) — the process view with the SEI module only. */
  seiDocuments: z.array(z.object({ seiProcessId: Id, title: z.string().nullable(), docDate: IsoDate.nullable() })).optional(),
});
export const HumanState = z.object({
  acknowledgedBy: z.string().nullable(),
  acknowledgedAt: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  resolvedReason: z.string().nullable(),
});
/** What triggered an obligation: the event and, when the DOU carried it, the act with its signed-page link. */
export const ObligationSource = z.object({
  eventId: Id,
  occurredOn: IsoDate.nullable(),
  description: z.string().nullable(),
  observation: z.string().nullable(),
  publication: z.string().nullable(),
  dou: z.object({
    pubDate: IsoDate,
    pubName: z.string(),
    editionNumber: z.string(),
    artType: z.string().nullable(),
    identifica: z.string().nullable(),
    ementa: z.string().nullable(),
    pdfPage: z.string().nullable(),
    segment: z.string().nullable(),
  }).nullable(),
});
export const ObligationView = z.object({
  id: Id,
  /** Null only for a manual Prazo on an administrative SEI process (no mining process). */
  processId: Id.nullable(),
  canonicalRef: z.string().nullable(),
  ruleId: z.string(),
  kind: z.string(),
  tier: z.string(),
  provenance: z.string(),
  confidence: z.enum(['high', 'medium', 'low']),
  status: z.enum(['open', 'satisfied', 'withdrawn', 'missed', 'kept_alive']),
  startsOn: IsoDate.nullable(),
  dueOn: IsoDate.nullable(),
  legalDueOn: IsoDate.nullable(),
  dueTags: z.array(z.string()),
  flags: z.array(z.string()),
  basis: z.string(),
  severity: z.enum(['act_now', 'note', 'ignore']),
  /** Days from today to `dueOn` (negative = overdue); null when undated. */
  daysLeft: z.number().int().nullable(),
  state: HumanState.nullable(),
  /** Null for obligations no event triggered (title expiry, the yearly calendar). */
  source: ObligationSource.nullable(),
  /** A manual Prazo (provenance `manual_sei`, Plan 6 Task 7): the SEI document the user registered it from. */
  sei: z.object({ seiProcessId: Id, seiDocumentId: Id, nup: z.string(), document: z.string().nullable() }).nullable(),
  /** A titular's licence on none of its mining processes (provenance `outside_licence`, Ambiental): the agency's
   *  label, where and what for — the deadline (renewal request, else expiry) is computed when read, never stored. */
  licence: z.object({ agency: z.string(), municipality: z.string().nullable(), activity: z.string().nullable(), link: z.string().url().nullable() }).nullable().optional(),
});
export const ProcessLinks = z.object({
  nup: z.string().nullable(),
  dou: z.array(z.object({ pubDate: IsoDate, pdfPage: z.string().nullable(), identifica: z.string().nullable() })),
});

/**
 * ANM open-data finance figures for a process (Plan 6), each with the date its source carries: TAH payments (the open
 * file is complete only to 2023), dívida ativa (a snapshot), CFEM paid per month (current), CFEM fines.
 */
export const ProcessFinance = z.object({
  /** TAH collection notices published in the DOU (Plan 6 Task 4b): the current debts ANM is charging, newest first. */
  tahNotices: z.array(z.object({
    publishedOn: IsoDate, kind: z.enum(['notificacao', 'decisao_sem_defesa']), noticeNumber: z.string(), amountCents: z.number().nullable(),
    tahDueOn: IsoDate.nullable(), collectionNup: z.string().nullable(), payUrl: z.string().nullable(), pdfPage: z.string().nullable(),
  })),
  tah: z.array(z.object({ settledOn: IsoDate.nullable(), hectares: z.number().nullable(), paidCents: z.number().nullable(), totalCents: z.number().nullable() })),
  /** The current titular's dívida ativa (keyed by debtor: every inscription, not only this process's). */
  debts: z.array(z.object({ revenue: z.string().nullable(), cda: z.string().nullable(), amountCents: z.number().nullable(), status: z.string().nullable(), updatedOn: IsoDate.nullable() })),
  cfem: z.array(z.object({ year: z.number().int(), month: z.number().int(), substance: z.string().nullable(), paidCents: z.number() })),
  cfemFines: z.array(z.object({ year: z.number().int().nullable(), month: z.number().int().nullable(), autoNumber: z.string().nullable(), amountCents: z.number().nullable(), substance: z.string().nullable() })),
});

export const ProcessoParams = z.object({ processId: Id });
/** An alert link sent before Plan 6 Task 6b (`/processos/<sequential id>`) → the process's public id (90 days). */
export const LegacyProcessParams = z.object({ n: z.string().regex(/^\d{1,18}$/) });
export const LegacyProcessResponse = z.object({ processId: Id });
/** Environmental agencies on a process (Plan 6 Task 8, Ambiental): licences and licensing requests. */
export const EnvRequestStep = z.object({ seq: z.number().int(), at: z.string(), from: z.string(), to: z.string(), reason: z.string() });
export const EnvRequestView = z.object({
  authority: z.string(),
  /** As the agency shows it ("26.690.214-0"). */
  number: z.string(),
  /** How we know it: a licence's own, attached by the agency, listed by it, or typed by this customer. */
  source: z.enum(['licence', 'attached', 'listed', 'manual']),
  filedOn: IsoDate.nullable(),
  status: z.enum(['filed', 'in_analysis', 'documents_requested', 'granted', 'refused', 'archived', 'suspended']).nullable(),
  statusSince: IsoDate.nullable(),
  location: z.string().nullable(),
  lastStepOn: IsoDate.nullable(),
  steps: z.array(EnvRequestStep),
  decision: z.string().nullable(),
  decidedOn: IsoDate.nullable(),
  /** The agency knows no such number (a typo). */
  missing: z.boolean(),
  checkedOn: IsoDate.nullable(),
  /** This customer's watch (null: public — the agency tied it to a licence on this process, or confirmed the titular). */
  watchId: Id.nullable(),
  verified: z.boolean(),
  /** The agency's public page of it (IAT-PR: e-Protocolo's consulta); null when the agency has none. */
  link: z.string().nullable(),
});
export const EnvLicenceView = z.object({
  authority: z.string(),
  /** The agency's id for it (IMA-SC `fce`, IAT-PR protocol); null for one registered by hand. */
  requestId: z.string().nullable(),
  type: z.string().nullable(),
  number: z.string().nullable(),
  issuedOn: IsoDate.nullable(),
  validUntil: IsoDate.nullable(),
  status: z.string().nullable(),
  /** It left the agency's list of the holder's licences before its validity (observed that day). */
  delistedOn: IsoDate.nullable(),
  activity: z.string().nullable(),
  /** A licence this customer registered (null: the agency's). */
  manualId: Id.nullable(),
  /** An agency licence this customer confirmed as its own (a suggestion answered "é minha"): the review id. */
  confirmedId: Id.nullable(),
  /** The agency's public page of it (IAT-PR: SGA's consulta pública); null when the agency has none we can link. */
  link: z.string().url().nullable(),
});
/** An agency licence inside the process's polygon under another name (below the match threshold): "possível licença sua". */
export const EnvSuggestionView = z.object({
  authority: z.string(),
  requestId: z.string(),
  type: z.string().nullable(),
  number: z.string().nullable(),
  issuedOn: IsoDate.nullable(),
  validUntil: IsoDate.nullable(),
  status: z.string().nullable(),
  enterprise: z.string().nullable(),
  activity: z.string().nullable(),
  similarity: z.number(),
});
export const ReviewSuggestionBody = z.object({ authority: z.string().regex(/^[a-z_]{2,20}$/), requestId: z.string().min(1).max(40), verdict: z.enum(['mine', 'not_mine']).nullable() });
export const ReviewSuggestionResponse = z.object({ verdict: z.enum(['mine', 'not_mine']).nullable() });
export type EnvSuggestionView = z.infer<typeof EnvSuggestionView>;
export type ReviewSuggestionBody = z.infer<typeof ReviewSuggestionBody>;
export const EnvAgencyView = z.object({ id: z.string(), label: z.string(), trackable: z.boolean(), example: z.string().nullable() });
export const ProcessAmbientalSection = z.object({
  /** `locked` without the Ambiental module (the lists are empty). */
  status: z.enum(['locked', 'open']),
  /** The agencies of the process's state. */
  agencies: z.array(EnvAgencyView),
  licences: z.array(EnvLicenceView),
  requests: z.array(EnvRequestView),
  /** Possible licences of the titular (map match, other name), not yet answered by this customer. */
  suggestions: z.array(EnvSuggestionView),
  canEdit: z.boolean(),
});
export const EnvBaseEnum = z.enum(['LP', 'LI', 'LO', 'LAS', 'LAC', 'AA']);
export const TrackRequestBody = z.object({ authority: z.string().regex(/^[a-z_]{2,20}$/), number: z.string().trim().min(3).max(40) });
export const TrackRequestResponse = z.object({ watchId: Id, number: z.string(), created: z.boolean() });
export const ManualLicenceBody = z.object({
  authority: z.string().regex(/^[a-z_]{2,20}$/),
  base: EnvBaseEnum,
  number: z.string().trim().max(60).nullable().optional(),
  issuedOn: IsoDate.nullable().optional(),
  validUntil: IsoDate,
  notes: z.string().trim().max(500).nullable().optional(),
});
export const ManualLicenceResponse = z.object({ id: Id });
export const EnvWatchParams = z.object({ processId: Id, watchId: Id });
export const EnvManualLicenceParams = z.object({ processId: Id, licenceId: Id });
export type EnvRequestView = z.infer<typeof EnvRequestView>;
export type EnvLicenceView = z.infer<typeof EnvLicenceView>;
export type ProcessAmbientalSection = z.infer<typeof ProcessAmbientalSection>;
export type TrackRequestBody = z.infer<typeof TrackRequestBody>;
export type ManualLicenceBody = z.infer<typeof ManualLicenceBody>;

export const ProcessoResponse = z.discriminatedUnion('layer', [
  z.object({ layer: z.literal('public'), raw: RawTimeline }),
  z.object({
    layer: z.literal('full'),
    via: z.enum(['holder', 'subrogee']),
    process: ProcessSummary,
    geometry: ProcessGeometry.nullable(),
    bbox: Bbox.nullable(),
    titles: z.array(RawTitle),
    parties: z.array(Party),
    events: z.array(TimelineEvent),
    obligations: z.array(ObligationView),
    links: ProcessLinks,
    finance: ProcessFinance,
    /** Restriction areas over the process (Plan 6 Task 6) — only the layers the subscription's modules open. */
    restrictions: z.array(RestrictionFindingView),
    /** Certified land parcels under the process (Plan 6 Task 6c) — only with the Território module. */
    parcels: z.array(ParcelFindingView),
    /** CAR properties under the process (Plan 6 Task 6c) — only with the Ambiental module; null otherwise. */
    car: CarResultView.nullable(),
    /** The process on SEI (Plan 6 Task 7) — `locked` without the SEI module. */
    sei: ProcessSeiSection,
    /** State environmental agencies (Plan 6 Task 8) — `locked` without the Ambiental module. */
    ambiental: ProcessAmbientalSection,
    freshness: Freshness,
  }),
]);

/** One or more AnmEntity ids, comma-separated (the global filter, spec §4.3 item 9). Absent = every visible entity. */
const U = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
export const AnmEntityFilter = z.object({ anmEntity: z.string().regex(new RegExp(`^${U}(,${U})*$`, 'i')).optional() });

export const Movement = TimelineEvent.extend({ processId: Id, canonicalRef: z.string().nullable(), severity: z.enum(['act_now', 'note', 'ignore']) });
/** An IBAMA record against a titular that falls inside none of its processes (Plan 6 Task 5; Ambiental module). */
export const IbamaRecord = z.object({
  kind: z.enum(['auto', 'embargo', 'notificacao']), number: z.string().nullable(), issuedOn: IsoDate.nullable(),
  valueCents: z.number().nullable(), description: z.string().nullable(), infractionType: z.string().nullable(),
  prazoDays: z.number().int().nullable(), attended: z.boolean().nullable(), lifted: z.boolean().nullable(), liftedOn: IsoDate.nullable(),
  ibamaNup: z.string().nullable(), municipality: z.string().nullable(), uf: z.string().nullable(), anmEntityId: Id,
});
/** A titular's licence on none of its processes (Plan 6 Task 8, Ambiental) — "fora dos processos". */
export const EnvOutsideItem = z.object({
  anmEntityId: Id, authority: z.string(), type: z.string().nullable(), number: z.string().nullable(), issuedOn: IsoDate.nullable(),
  validUntil: IsoDate.nullable(), delistedOn: IsoDate.nullable(), activity: z.string().nullable(), municipality: z.string().nullable(),
  /** The agency's public page of it (as EnvLicenceView.link). */
  link: z.string().url().nullable(),
});
export type EnvOutsideItem = z.infer<typeof EnvOutsideItem>;
export const HojeResponse = z.object({
  today: IsoDate,
  anmEntities: z.array(Id),
  movements: z.array(Movement),
  actNow: z.array(ObligationView),
  ibamaOutside: z.array(IbamaRecord),
  /** State agencies' licences of the titulares on none of their processes (Ambiental). */
  envOutside: z.array(EnvOutsideItem),
  freshness: Freshness,
});

export const PrazosQuery = AnmEntityFilter.extend({ from: IsoDate.optional(), to: IsoDate.optional() });
export const PrazosResponse = z.object({
  today: IsoDate,
  from: IsoDate,
  to: IsoDate,
  anmEntities: z.array(Id),
  /** By provenance tier, most confident first (spec §7.1). Undated and overdue obligations are always included. */
  groups: z.array(z.object({ tier: z.string(), obligations: z.array(ObligationView) })),
  freshness: Freshness,
});

export const ObligationParams = z.object({ obligationId: Id });
export const ManualPrazoParams = z.object({ manualPrazoId: Id });
export const ResolveBody = z.object({ reason: z.string().trim().min(3).max(500) });
export const ObligationStateResponse = z.object({ obligationId: Id, subscriptions: z.array(Id), state: HumanState });

// Inferred types (the web's typed client and screens).
export type RawEvent = z.infer<typeof RawEvent>;
export type WithheldWindow = z.infer<typeof WithheldWindow>;
export type Party = z.infer<typeof Party>;
export type ProcessSummary = z.infer<typeof ProcessSummary>;
export type RawTimeline = z.infer<typeof RawTimeline>;
export type ProcessGeometry = z.infer<typeof ProcessGeometry>;
export type Bbox = z.infer<typeof Bbox>;
export type ConsultaResponse = z.infer<typeof ConsultaResponse>;
export type TimelineEvent = z.infer<typeof TimelineEvent>;
export type HumanState = z.infer<typeof HumanState>;
export type ObligationView = z.infer<typeof ObligationView>;
export type ProcessoResponse = z.infer<typeof ProcessoResponse>;
export type ProcessFinance = z.infer<typeof ProcessFinance>;
export type Movement = z.infer<typeof Movement>;
export type HojeResponse = z.infer<typeof HojeResponse>;
export type IbamaRecord = z.infer<typeof IbamaRecord>;
export type PrazosResponse = z.infer<typeof PrazosResponse>;
export type ObligationStateResponse = z.infer<typeof ObligationStateResponse>;

// --- Onboarding (Plan 4b Task 10): "digite seu nome ou CNPJ" → AnmEntity groups to monitor ------------------------------

export const OnboardingQuery = z.object({ q: z.string().trim().min(2).max(120) });
/** One holder (AnmEntity) with what it holds today — public data only, no obligations (free layer). */
export const OnboardingGroup = z.object({
  anmEntityId: Id,
  name: z.string(),
  /** As ANM publishes it (a CPF masked). */
  document: z.string().nullable(),
  processCount: z.number().int().nonnegative(),
  ufs: z.array(z.string()),
  phases: z.array(z.object({ phase: z.string(), count: z.number().int().nonnegative() })),
  /** The acting customer already has a live subscription on it (or operates one by subrogation). */
  monitored: z.boolean(),
});
export const OnboardingResponse = z.object({
  query: z.string(),
  /** How the query was read: a process (its current holder), a document, a name, or nothing usable. */
  kind: z.enum(['processo', 'documento', 'nome', 'nenhum']),
  groups: z.array(OnboardingGroup),
  /** People found who hold nothing (e.g. only RT) — "encontramos você como responsável técnico". */
  asTechnicalOnly: z.number().int().nonnegative(),
});
export type OnboardingGroup = z.infer<typeof OnboardingGroup>;
export type OnboardingResponse = z.infer<typeof OnboardingResponse>;
